import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import moment from "moment";
import { Link } from "react-router-dom";
import type { CashMovement } from "../cash-movement.entity";
import type { Sell } from "../../sell/sell.entity";
import { SellPaymentMethod } from "../../sell/sell.entity";

const money = (n: number) =>
  n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

type Props = {
  openingCash: number;
  movements: CashMovement[];
  sells?: Sell[];
  /** Quando passado, exibe banner informativo com botão de link para os detalhes. */
  detailLink?: string;
};

type LogEntry =
  | { kind: "movement"; item: CashMovement; time: number }
  | { kind: "sell"; item: Sell; time: number };

export function CashMovementsLogView({ openingCash, movements, sells = [], detailLink }: Props) {
  const theme = useTheme();

  // Apenas vendas em dinheiro concluídas entram no log
  const cashSells = sells.filter(
    (s) => s.getCompletedAt() !== null && s.getPaymentMethod() === SellPaymentMethod.MONEY
  );

  const entries: LogEntry[] = [
    ...movements.map((m) => ({
      kind: "movement" as const,
      item: m,
      time: m.getCreatedAt().valueOf(),
    })),
    ...cashSells.map((s) => ({
      kind: "sell" as const,
      item: s,
      time: s.getCompletedAt()!.valueOf(),
    })),
  ].sort((a, b) => a.time - b.time);

  const totalEntries = movements
    .filter((m) => m.getType() === "entry")
    .reduce((s, m) => s + m.getAmount(), 0);

  const totalWithdrawals = movements
    .filter((m) => m.getType() === "withdrawal")
    .reduce((s, m) => s + m.getAmount(), 0);

  const totalCashSales = cashSells.reduce((s, sell) => s + sell.getSellTotal(), 0);

  const currentBalance = openingCash + totalEntries - totalWithdrawals + totalCashSales;

  return (
    <Stack spacing={0}>
      {/* ── Banner informativo ── */}
      {detailLink && (
        <Alert
          severity="info"
          sx={{ mb: 1.5, borderRadius: 2, alignItems: "flex-start" }}
          action={
            <Button
              component={Link}
              to={detailLink}
              size="small"
              color="info"
              endIcon={<OpenInNewIcon fontSize="small" />}
              sx={{ textTransform: "none", fontWeight: 700, whiteSpace: "nowrap" }}
            >
              Ver detalhes
            </Button>
          }
        >
          Exibindo apenas vendas em dinheiro. Para ver todas as vendas e formas de pagamento, acesse os detalhes da sessão.
        </Alert>
      )}

      {/* ── Abertura ── */}
      <MovementRow
        icon={
          <LockOpenIcon
            fontSize="small"
            sx={{ color: "success.main", fontSize: 15 }}
          />
        }
        iconBg={alpha(theme.palette.success.main, 0.12)}
        label="Abertura do caixa"
        sublabel={null}
        amount={openingCash}
        amountColor="success.main"
        prefix="+"
        time={null}
      />

      {entries.map((entry) => {
        if (entry.kind === "movement") {
          const m = entry.item;
          const isEntry = m.getType() === "entry";
          return (
            <MovementRow
              key={`m-${m.id}`}
              icon={
                isEntry ? (
                  <ArrowUpwardIcon sx={{ color: "success.main", fontSize: 15 }} />
                ) : (
                  <ArrowDownwardIcon sx={{ color: "error.main", fontSize: 15 }} />
                )
              }
              iconBg={
                isEntry
                  ? alpha(theme.palette.success.main, 0.1)
                  : alpha(theme.palette.error.main, 0.1)
              }
              label={m.getReason()}
              sublabel={m.getCategoryLabel()}
              amount={m.getAmount()}
              amountColor={isEntry ? "success.main" : "error.main"}
              prefix={isEntry ? "+" : "−"}
              time={moment(m.getCreatedAt()).format("HH:mm")}
            />
          );
        }

        const s = entry.item;
        return (
          <MovementRow
            key={`s-${s.id}`}
            icon={
              <ShoppingCartOutlinedIcon
                sx={{ color: "success.main", fontSize: 15 }}
              />
            }
            iconBg={alpha(theme.palette.success.main, 0.1)}
            label="Venda — Dinheiro"
            sublabel={null}
            amount={s.getSellTotal()}
            amountColor="success.main"
            prefix="+"
            time={moment(s.getCompletedAt()).format("HH:mm")}
          />
        );
      })}

      {/* ── Saldo atual ── */}
      {entries.length > 0 && (
        <>
          <Divider sx={{ my: 1 }} />
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ px: 1, py: 0.75 }}
          >
            <Typography variant="body2" color="text.secondary" fontWeight={600}>
              Saldo atual em caixa
            </Typography>
            <Typography
              variant="body1"
              fontWeight={800}
              color={currentBalance >= 0 ? "text.primary" : "error.main"}
            >
              {money(currentBalance)}
            </Typography>
          </Stack>
        </>
      )}
    </Stack>
  );
}

type RowProps = {
  icon: React.ReactNode;
  iconBg: string;
  label: string;
  sublabel: string | null;
  amount: number;
  amountColor: string;
  prefix: "+" | "−" | " ";
  time: string | null;
};

function MovementRow({ icon, iconBg, label, sublabel, amount, amountColor, prefix, time }: RowProps) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        py: 1.25,
        px: 0.5,
        borderRadius: 2,
        "&:hover": { bgcolor: "action.hover" },
      }}
    >
      <Box
        sx={{
          width: 30,
          height: 30,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: iconBg,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600} noWrap>
          {label}
        </Typography>
        {(sublabel || time) && (
          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
            {sublabel && (
              <Chip
                label={sublabel}
                size="small"
                variant="outlined"
                sx={{ height: 17, fontSize: "0.63rem", px: 0 }}
              />
            )}
            {time && (
              <Typography variant="caption" color="text.secondary">
                {time}
              </Typography>
            )}
          </Stack>
        )}
      </Box>

      <Typography
        variant="body2"
        fontWeight={800}
        color={amountColor}
        sx={{ flexShrink: 0 }}
      >
        {prefix !== " " ? `${prefix} ` : ""}{money(amount)}
      </Typography>
    </Box>
  );
}
